import { useEffect, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchSendingDevices,
  type SendingDevice,
} from "../api/messaging";

export const SENDING_DEVICES_QUERY_KEY = ["crm", "messaging", "devices"] as const;
export const SENDING_DEVICE_STORAGE_KEY = "phone-board:quick-sending-device-id";
const EMPTY_SENDING_DEVICES: SendingDevice[] = [];

let selectedDeviceSnapshot: string | null | undefined;
const selectedDeviceListeners = new Set<() => void>();

function selectedDeviceIdSnapshot(): string | null {
  if (selectedDeviceSnapshot === undefined) {
    selectedDeviceSnapshot =
      typeof window === "undefined"
        ? null
        : window.localStorage.getItem(SENDING_DEVICE_STORAGE_KEY);
  }
  return selectedDeviceSnapshot;
}

function subscribeSelectedDevice(listener: () => void): () => void {
  selectedDeviceListeners.add(listener);
  return () => selectedDeviceListeners.delete(listener);
}

/** 문자창 어디서 고르든 고객 표의 마지막 발송폰 선택과 같은 브라우저 값으로 기억한다. */
export function rememberSendingDevice(id: string): void {
  selectedDeviceSnapshot = id;
  window.localStorage.setItem(SENDING_DEVICE_STORAGE_KEY, id);
  selectedDeviceListeners.forEach((listener) => listener());
}

function formatSendingPhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  return value;
}

export function sendingDeviceDisplay(device: SendingDevice): string {
  return device.reportedPhoneNumber
    ? formatSendingPhone(device.reportedPhoneNumber)
    : device.phoneLineStatus === "manually_verified" && device.phoneNumber
      ? formatSendingPhone(device.phoneNumber)
      : device.label?.trim() || device.name?.trim() || "매장폰";
}

export type SendingDeviceOption = {
  id: string;
  display: string;
  online: boolean;
  phoneNumber: string | null;
  reportedPhoneNumber: string | null;
  manuallyVerifiedPhoneNumber: string | null;
  fallbackName: string;
};

export function toSendingDeviceOption(device: SendingDevice): SendingDeviceOption {
  return {
    id: device.id,
    display: sendingDeviceDisplay(device),
    online: device.online,
    phoneNumber: device.phoneNumber,
    reportedPhoneNumber: device.reportedPhoneNumber,
    manuallyVerifiedPhoneNumber: device.manuallyVerifiedPhoneNumber,
    fallbackName: device.label?.trim() || device.name?.trim() || "매장폰",
  };
}

/**
 * 매장폰 목록은 앱 세션에서 한 번만 받는다. 문자 카드가 새로 뜨거나 문자창을 다시 열어도
 * 모든 소비처가 같은 React Query 캐시를 읽고, 폰 관리가 invalidate할 때만 다시 받는다.
 */
export function useSendingDevices(enabled = true) {
  const query = useQuery({
    queryKey: SENDING_DEVICES_QUERY_KEY,
    queryFn: fetchSendingDevices,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  return {
    query,
    devices: query.data?.devices ?? EMPTY_SENDING_DEVICES,
  };
}

/** 고객 표는 종전처럼 마지막으로 고른 폰을 기본값으로 삼는다. */
export function useRememberedSendingDevice(devices: SendingDevice[]) {
  const selectedDeviceId = useSyncExternalStore(
    subscribeSelectedDevice,
    selectedDeviceIdSnapshot,
    () => null,
  );
  const selectedDevice = devices.find((device) => device.id === selectedDeviceId) ?? null;

  useEffect(() => {
    if (devices.length === 0 || selectedDevice) return;
    rememberSendingDevice(devices[0].id);
  }, [devices, selectedDevice]);

  return { selectedDeviceId, selectedDevice };
}
